import React, { useState } from 'react';
import { Modal, Button, Card, Form, Spinner, Badge, Alert, Row, Col, Accordion } from 'react-bootstrap';
import Swal from 'sweetalert2';
import { actualizarDatos } from '../../../api/common';

/**
 * ModalCorregirEquipos - Modal para corregir equipos con datos incompletos
 * @param {Object} props - Component props
 * @param {boolean} props.show - Control de visibilidad del modal
 * @param {Array} props.equiposProblematicos - Array de equipos sin sede o datos incompletos
 * @param {Array} props.listaSedes - Lista de sedes del cliente
 * @param {Function} props.onCorregir - Handler que se ejecuta cuando se corrige un equipo
 * @param {Function} props.onClose - Handler para cerrar modal (solo si todos están corregidos)
 * @param {Function} props.onCancelar - Handler para cancelar sin corregir equipos
 * @returns {JSX.Element}
 */
const ModalCorregirEquipos = ({ show, equiposProblematicos = [], listaSedes = [], onCorregir, onClose, onCancelar }) => {
    const [equiposCorregidos, setEquiposCorregidos] = useState(new Set());
    const [guardando, setGuardando] = useState({});
    const [formsData, setFormsData] = useState({});
    const [activeKey, setActiveKey] = useState('0');


    const totalEquipos = equiposProblematicos.length;
    const totalCorregidos = equiposCorregidos.size;
    const todosCorregidos = totalCorregidos === totalEquipos;

    // Inicializar forms data cuando cambian los equipos problemáticos
    React.useEffect(() => {
        const initialForms = {};
        equiposProblematicos.forEach(equipo => {
            initialForms[equipo._id] = {
                sedeClient: equipo.sedeClient?._id || '',
                marca: equipo.marca || '',
                modelo: equipo.modelo || '',
                serie: equipo.serie || '',
                inventario: equipo.inventario || '',
                ubicacion: equipo.ubicacion || '',
                resolucion: equipo.resolucion || '',
                puntos: equipo.puntos || '',
                observacion: equipo.observacion || ''
            };
        });
        setFormsData(initialForms);
    }, [equiposProblematicos]);

    const handleInputChange = (equipoId, field, value) => {
        setFormsData(prev => ({
            ...prev,
            [equipoId]: {
                ...prev[equipoId],
                [field]: value
            }
        }));
    };

    const handleGuardar = async (equipo) => {
        const formData = formsData[equipo._id];

        console.log('💾 ModalCorregirEquipos: Guardando equipo', equipo._id);
        console.log('💾 Datos a guardar:', formData);

        // Validación
        if (!formData.sedeClient) {
            Swal.fire('Error', 'La sede es requerida', 'error');
            return;
        }

        setGuardando(prev => ({ ...prev, [equipo._id]: true }));

        try {
            // Actualizar equipo en BD
            const response = await actualizarDatos('Equipos', formData, equipo._id);

            // Buscar la sede completa para actualizar el objeto
            const sedeCompleta = listaSedes.find(s => s._id === formData.sedeClient);
            console.log('🏢 Sede encontrada:', sedeCompleta);

            // Preparar datos actualizados con sede poblada - INCLUIR EQUIPO COMPLETO
            const equipoActualizado = {
                ...equipo,  // ✅ Todos los campos originales (item, cliente, _id, etc.)
                ...formData,  // ✅ Sobrescribir con correcciones del formulario
                sedeClient: sedeCompleta || { _id: formData.sedeClient, nameSede: 'Desconocida' }
            };
            
            console.log('📤 Llamando onCorregir con:', equipoActualizado);

            // Notificar al componente padre
            await onCorregir(equipo._id, equipoActualizado);

            // Marcar como corregido
            setEquiposCorregidos(prev => new Set([...prev, equipo._id]));

            Swal.fire({
                icon: 'success',
                title: 'Equipo corregido',
                text: 'El equipo se actualizó correctamente',
                timer: 1500,
                showConfirmButton: false
            });

        } catch (error) {
            console.error('Error actualizando equipo:', error);
            Swal.fire('Error', error.response?.data?.message || 'No se pudo actualizar el equipo', 'error');
        } finally {
            setGuardando(prev => ({ ...prev, [equipo._id]: false }));
        }
    };

    const handleCerrar = () => {
        console.log('🚪 ModalCorregirEquipos: Intentando cerrar. Todos corregidos?', todosCorregidos);
        
        if (!todosCorregidos) {
            Swal.fire({
                icon: 'warning',
                title: 'Equipos pendientes',
                text: `Aún quedan ${totalEquipos - totalCorregidos} equipo(s) sin corregir. Debe corregir todos para continuar.`,
            });
            return;
        }
        
        console.log('✅ ModalCorregirEquipos: Todos corregidos, cerrando modal y limpiando estado');
        
        // Limpiar estado y cerrar
        setEquiposCorregidos(new Set());
        setFormsData({});
        onClose();
    };

    const handleCancelar = () => {
        console.log('❌ ModalCorregirEquipos: Cancelando sin corregir');
        
        // Limpiar estado
        setEquiposCorregidos(new Set());
        setFormsData({});
        
        // Llamar al handler de cancelación
        if (onCancelar) {
            onCancelar();
        }
    };

    return (
        <Modal show={show} onHide={handleCerrar} size="lg" backdrop="static" keyboard={false}>
            <Modal.Header closeButton={todosCorregidos}>
                <Modal.Title>
                    <i className="bi bi-exclamation-triangle-fill text-warning me-2"></i>
                    Corregir Equipos con Datos Incompletos
                </Modal.Title>
            </Modal.Header>
            <Modal.Body
                style={{maxHeight:'80vh', overflowY:'auto'}}
            >
                <Alert variant="warning">
                    <strong>Atención:</strong> Los siguientes equipos no tienen información de sede completa. 
                    Por favor, corrija la información antes de continuar.
                </Alert>

                {/* Indicador de progreso */}
                <div className="mb-3 p-3 bg-light rounded">
                    <div className="d-flex justify-content-between align-items-center">
                        <div>
                            <strong>Progreso de corrección:</strong>
                        </div>
                        <Badge bg={todosCorregidos ? 'success' : 'warning'} style={{ fontSize: '1rem' }}>
                            {totalCorregidos} / {totalEquipos}
                        </Badge>
                    </div>
                </div>

                {/* Accordion con equipos */}
                <Accordion activeKey={activeKey} onSelect={(key) => setActiveKey(key)}>
                    {equiposProblematicos.map((equipo, index) => {
                        const estaCorregido = equiposCorregidos.has(equipo._id);
                        const estaGuardando = guardando[equipo._id];
                        const formData = formsData[equipo._id] || {};

                        return (
                            <Accordion.Item key={equipo._id} eventKey={index.toString()}>
                                <Accordion.Header>
                                    <div className="d-flex justify-content-between w-100 me-3">
                                        <div>
                                            {estaCorregido && (
                                                <i className="bi bi-check-circle-fill text-success me-2"></i>
                                            )}
                                            <strong>{equipo.item?.nombre || 'N/A'}</strong>
                                            <span className="text-muted ms-2">
                                                {equipo.marca} - {equipo.modelo} - {equipo.serie}
                                            </span>
                                        </div>
                                        <div>
                                            {estaCorregido ? (
                                                <Badge bg="success">Corregido</Badge>
                                            ) : (
                                                <Badge bg="danger">Pendiente</Badge>
                                            )}
                                        </div>
                                    </div>
                                </Accordion.Header>
                                <Accordion.Body>
                                    <Form>
                                        <Row>
                                            <Col md={6}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label>
                                                        Sede <span className="text-danger">*</span>
                                                    </Form.Label>
                                                    <Form.Select
                                                        value={formData.sedeClient || ''}
                                                        onChange={(e) => handleInputChange(equipo._id, 'sedeClient', e.target.value)}
                                                        disabled={estaCorregido}
                                                        required
                                                    >
                                                        <option value="">Seleccione una sede...</option>
                                                        {listaSedes.map(sede => (
                                                            <option key={sede._id} value={sede._id}>
                                                                {sede.nameSede}
                                                            </option>
                                                        ))}
                                                    </Form.Select>
                                                </Form.Group>
                                            </Col>

                                            <Col md={6}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label>Marca</Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        value={formData.marca || ''}
                                                        onChange={(e) => handleInputChange(equipo._id, 'marca', e.target.value)}
                                                        disabled={estaCorregido}
                                                    />
                                                </Form.Group>
                                            </Col>

                                            <Col md={6}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label>Modelo</Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        value={formData.modelo || ''}
                                                        onChange={(e) => handleInputChange(equipo._id, 'modelo', e.target.value)}
                                                        disabled={estaCorregido}
                                                    />
                                                </Form.Group>
                                            </Col>

                                            <Col md={6}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label>Serie</Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        value={formData.serie || ''}
                                                        onChange={(e) => handleInputChange(equipo._id, 'serie', e.target.value)}
                                                        disabled={estaCorregido}
                                                    />
                                                </Form.Group>
                                            </Col>

                                            <Col md={6}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label>Inventario</Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        value={formData.inventario || ''}
                                                        onChange={(e) => handleInputChange(equipo._id, 'inventario', e.target.value)}
                                                        disabled={estaCorregido}
                                                    />
                                                </Form.Group>
                                            </Col>

                                            <Col md={6}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label>Ubicación</Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        value={formData.ubicacion || ''}
                                                        onChange={(e) => handleInputChange(equipo._id, 'ubicacion', e.target.value)}
                                                        disabled={estaCorregido}
                                                    />
                                                </Form.Group>
                                            </Col>

                                            <Col md={6}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label>Resolución</Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        value={formData.resolucion || ''}
                                                        onChange={(e) => handleInputChange(equipo._id, 'resolucion', e.target.value)}
                                                        disabled={estaCorregido}
                                                    />
                                                </Form.Group>
                                            </Col>

                                            <Col md={6}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label>Puntos a Calibrar</Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        value={formData.puntos || ''}
                                                        onChange={(e) => handleInputChange(equipo._id, 'puntos', e.target.value)}
                                                        disabled={estaCorregido}
                                                    />
                                                </Form.Group>
                                            </Col>

                                            <Col md={12}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label>Observación</Form.Label>
                                                    <Form.Control
                                                        as="textarea"
                                                        rows={2}
                                                        value={formData.observacion || ''}
                                                        onChange={(e) => handleInputChange(equipo._id, 'observacion', e.target.value)}
                                                        disabled={estaCorregido}
                                                    />
                                                </Form.Group>
                                            </Col>
                                        </Row>

                                        <div className="d-grid">
                                            <Button
                                                variant={estaCorregido ? 'success' : 'primary'}
                                                onClick={() => handleGuardar(equipo)}
                                                disabled={estaCorregido || estaGuardando}
                                            >
                                                {estaGuardando ? (
                                                    <>
                                                        <Spinner
                                                            as="span"
                                                            animation="border"
                                                            size="sm"
                                                            role="status"
                                                            aria-hidden="true"
                                                            className="me-2"
                                                        />
                                                        Guardando...
                                                    </>
                                                ) : estaCorregido ? (
                                                    <>
                                                        <i className="bi bi-check-circle me-2"></i>
                                                        Equipo Corregido
                                                    </>
                                                ) : (
                                                    <>
                                                        <i className="bi bi-save me-2"></i>
                                                        Guardar Corrección
                                                    </>
                                                )}
                                            </Button>
                                        </div>
                                    </Form>
                                </Accordion.Body>
                            </Accordion.Item>
                        );
                    })}
                </Accordion>
            </Modal.Body>
            <Modal.Footer>
                <div className="d-flex justify-content-between w-100 align-items-center">
                    <div>
                        {todosCorregidos ? (
                            <span className="text-success">
                                <i className="bi bi-check-circle-fill me-2"></i>
                                Todos los equipos han sido corregidos
                            </span>
                        ) : (
                            <span className="text-warning">
                                <i className="bi bi-exclamation-triangle-fill me-2"></i>
                                Faltan {totalEquipos - totalCorregidos} equipo(s) por corregir
                            </span>
                        )}
                    </div>
                    <div>
                        <Button 
                            variant="danger" 
                            onClick={handleCancelar}
                            className="me-2"
                        >
                            <i className="bi bi-x-circle me-2"></i>
                            Cancelar sin Corregir
                        </Button>
                        <Button 
                            variant={todosCorregidos ? 'success' : 'secondary'} 
                            onClick={handleCerrar}
                            disabled={!todosCorregidos}
                        >
                            {todosCorregidos ? 'Continuar' : 'Debe corregir todos los equipos'}
                        </Button>
                    </div>
                </div>
            </Modal.Footer>
        </Modal>
    );
};

export default ModalCorregirEquipos;
